import { Injectable, OnDestroy } from '@angular/core';
import { Unsubscribe } from 'firebase/firestore';
import { BehaviorSubject } from 'rxjs';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { LoggingService } from 'src/app/services/logging/logging.service';
import { UserService } from 'src/app/services/user/user.service';

const LOCAL_STORAGE_KEY = 'muted_conversation_ids';

@Injectable({
  providedIn: 'root'
})
export class MuteService implements OnDestroy {

  public mutedIds$ = new BehaviorSubject<Set<string>>(new Set());

  private listener: Unsubscribe;

  constructor(
    private firebase: FirebaseService,
    private localStorage: LocalStorageService,
    private logging: LoggingService,
    private user: UserService,
  ) {}

  ngOnDestroy(): void {
    this.stopListening();
  }

  public init(): void {
    // Load from localStorage immediately for fast startup
    const cached = this.loadFromLocalStorage();
    this.mutedIds$.next(cached);

    // Start Firestore listener for cross-device sync (source of truth)
    this.listenToFirestore();
  }

  public async mute(conversationId: string): Promise<void> {
    const ids = new Set(this.mutedIds$.getValue());
    ids.add(conversationId);
    this.mutedIds$.next(ids);
    this.persistToLocalStorage(ids);
    await this.persistToFirestore(ids);
  }

  public async unmute(conversationId: string): Promise<void> {
    const ids = new Set(this.mutedIds$.getValue());
    ids.delete(conversationId);
    this.mutedIds$.next(ids);
    this.persistToLocalStorage(ids);
    await this.persistToFirestore(ids);
  }

  public async toggleMute(conversationId: string): Promise<void> {
    if (this.isMuted(conversationId)) {
      return this.unmute(conversationId);
    }
    return this.mute(conversationId);
  }

  public isMuted(conversationId: string): boolean {
    return this.mutedIds$.getValue().has(conversationId);
  }

  public stopListening(): void {
    if (this.listener) {
      this.listener();
      this.listener = null;
    }
  }

  private loadFromLocalStorage(): Set<string> {
    try {
      const raw = this.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return new Set(parsed);
        }
      }
    } catch {
      this.logging.warning('MuteService: Failed to parse localStorage muted IDs');
    }
    return new Set();
  }

  private persistToLocalStorage(ids: Set<string>): void {
    this.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([...ids]));
  }

  private async persistToFirestore(ids: Set<string>): Promise<void> {
    if (!this.user.user?.uid) {
      return;
    }
    try {
      await this.firebase.firestore.docSet(
        `users/${this.user.user.uid}`,
        { mutedConversationIds: [...ids] },
        { merge: true },
      );
    } catch (err) {
      this.logging.warning('MuteService: Failed to persist to Firestore', err);
    }
  }

  private listenToFirestore(): void {
    this.stopListening();
    if (!this.user.user?.uid) {
      return;
    }

    this.listener = this.firebase.firestore.docListen(
      `users/${this.user.user.uid}`,
      (snapshot) => {
        if (!snapshot.exists()) {
          return;
        }
        const data = snapshot.data();
        const firestoreIds: string[] = data?.mutedConversationIds || [];
        const newSet = new Set(firestoreIds);
        // Only update if different from current state to avoid loops
        const current = this.mutedIds$.getValue();
        if (this.setsAreEqual(current, newSet)) {
          return;
        }
        this.mutedIds$.next(newSet);
        this.persistToLocalStorage(newSet);
      },
    );
  }

  private setsAreEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  }
}
